import { apiClient } from './client';
import type { SalesRepSlug } from '../pages/ProjectManagement/team';

export type DealStage =
  | 'prospecting'
  | 'discovery'
  | 'qualification'
  | 'considering'
  | 'proposal'
  | 'proposal_out'
  | 'negotiation'
  | 'handshake'
  | 'closed_won'
  | 'closed_lost'
  /** An Attio stage the sync doesn't recognise. */
  | 'unknown';

export interface StageBucket {
  stage: DealStage;
  count: number;
  valueEur: number;
}

/** Mirrors packages/api/src/modules/project-management/deal-classification.ts */
export type DealKind = 'new' | 'expansion' | 'renewal' | 'pilot' | 'other';

export interface KindBucket {
  count: number;
  valueEur: number;
}

export type ByKind = Record<DealKind, KindBucket>;

/** Open pipeline today, split by whether the stage moved in the last 90 days. */
export interface OpenSplit {
  openCount: number;
  openValueEur: number;
  activeCount: number;
  activeValueEur: number;
  staleCount: number;
  staleValueEur: number;
}

export interface RepBucket extends OpenSplit {
  repSlug: SalesRepSlug | 'other';
  label: string;
  email: string | null;
  /** Won in the period, only from the Attio go-live (8 Jan 2026). */
  wonCount: number;
  wonValueEur: number;
  wonByKind: ByKind;
}

export interface TopDeal {
  id: string;
  name: string;
  stage: DealStage;
  valueEur: number;
  companyName: string | null;
  ownerEmail: string | null;
  repSlug: SalesRepSlug | null;
  expectedCloseDate: string | null;
  kind: DealKind;
  /** Only set for closed-won deals: when the deal was marked won. */
  actualCloseDate?: string | null;
  /** The Attio deal record. Null for a deal not synced from Attio; older APIs omit it. */
  attioUrl?: string | null;
}

export interface StaleDeal {
  id: string;
  name: string;
  companyName: string | null;
  ownerEmail: string | null;
  repSlug: SalesRepSlug | null;
  stage: DealStage;
  valueEur: number;
  stageChangedAt: string | null;
  daysSinceStageChange: number | null;
  /** Latest Attio email or meeting with the deal's company or its people; older APIs omit it. */
  lastActivityAt?: string | null;
  daysSinceActivity?: number | null;
  expectedCloseDate: string | null;
  /** The Attio deal record. Null for a deal not synced from Attio; older APIs omit it. */
  attioUrl?: string | null;
}

export interface QuarterBucket {
  /** "2026-Q3", or "none" for deals without a close date. */
  quarter: string;
  label: string;
  count: number;
  valueEur: number;
  /** Open buckets: the part on Attio's default close date (15 Jan 2027), never dated. Older APIs omit it. */
  defaultCloseCount?: number;
  defaultCloseValueEur?: number;
}

export interface SalesPeriodFilter {
  /** YYYY-Qn values, summed. Empty when a custom range is used. */
  quarters: string[];
  /** Inclusive YYYY-MM-DD range on the Brussels calendar. */
  from: string | null;
  to: string | null;
}

export interface SalesOverview {
  generatedAt: string;
  /** Applies to won/lost only; open pipeline is always as of today. */
  filter: SalesPeriodFilter | null;
  staleAfterDays: number;
  totals: OpenSplit & {
    wonCount: number;
    wonValueEur: number;
    wonByKind: ByKind;
    wonBeforeAttioCount: number;
    wonBeforeAttioValueEur: number;
    lostCount: number;
  };
  byStage: StageBucket[];
  byRep: RepBucket[];
  topOpenDeals: TopDeal[];
  /** Every open deal today, sorted by value desc. */
  openDeals: TopDeal[];
  /** Open deals whose stage hasn't moved in staleAfterDays days. */
  staleDeals: StaleDeal[];
  /** Every closed-won deal in the period, sorted by value desc. */
  wonDeals: TopDeal[];
  openByQuarter: QuarterBucket[];
  wonByQuarter: QuarterBucket[];
}

export async function getSalesOverview(filter: SalesPeriodFilter | null): Promise<SalesOverview> {
  const params =
    filter?.from && filter.to
      ? { from: filter.from, to: filter.to }
      : filter && filter.quarters.length > 0
        ? { quarters: filter.quarters.join(',') }
        : {};
  return apiClient.get('/project-management/sales/overview', { params });
}

export interface TeamMemberSummary extends OpenSplit {
  repSlug: SalesRepSlug | 'other';
  label: string;
  email: string | null;
  /** Won since the Attio go-live (8 Jan 2026). */
  wonCount: number;
  wonValueEur: number;
  wonByKind: ByKind;
  byStage: StageBucket[];
}

export interface SalesTeam {
  generatedAt: string;
  staleAfterDays: number;
  members: TeamMemberSummary[];
}

export async function getSalesTeam(): Promise<SalesTeam> {
  return apiClient.get('/project-management/sales/team');
}

// --- Meetings (external AE meetings from the Attio calendar sync) ---

export interface AeMeeting {
  id: string;
  title: string | null;
  startTime: string;
  endTime: string | null;
  reps: SalesRepSlug[];
  external: Array<{ email: string; name: string | null; status: string | null }>;
  held: boolean;
  /**
   * Counted: prospect (Attio type) and booked (no type, but the company or person booked a meeting with us).
   * Not counted: excluded (customer, partner, supplier, investor) and untyped (no type, never booked).
   */
  audience: 'prospect' | 'excluded' | 'booked' | 'untyped';
  /** The outside people's companies, one per email domain; name/type null when Attio has none. */
  companies: Array<{
    domain: string;
    name: string | null;
    type: string | null;
    /** Attio record id for the link out. Older APIs omit it. */
    recordId?: string | null;
    /** 'supplier_rule' when the type comes from the known-supplier list, not Attio. Older APIs omit it. */
    typeFrom?: 'attio' | 'supplier_rule' | null;
  }>;
}

export interface MeetingsResponse {
  generatedAt: string;
  rangeFrom: string;
  rangeTo: string;
  /** Counted: prospect meetings plus meetings whose companies have no Attio type yet. */
  meetings: AeMeeting[];
  /** Not counted: customer, partner, supplier and investor meetings. */
  notCounted: AeMeeting[];
  lastSyncedAt: string | null;
  warnings: string[];
}

/**
 * The Meetings sources stop waiting after this long. The API answers a slow
 * load itself at 20–25 s with a readable 504; this is the backstop for when it
 * doesn't, so the page shows an error instead of a skeleton for minutes.
 */
export const MEETINGS_CLIENT_TIMEOUT_MS = 35_000;

/** Runs a Meetings request with MEETINGS_CLIENT_TIMEOUT_MS, turning the browser's abort into a sentence. */
export async function meetingsRequest<T>(what: string, send: (signal: AbortSignal) => Promise<T>): Promise<T> {
  try {
    return await send(AbortSignal.timeout(MEETINGS_CLIENT_TIMEOUT_MS));
  } catch (err) {
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      // The web lib target has no ErrorOptions, so the cause is attached by hand.
      throw Object.assign(new Error(`${what} didn't answer in ${MEETINGS_CLIENT_TIMEOUT_MS / 1000} s. Try again; if it keeps happening, report it.`), {
        cause: err,
      });
    }
    throw err;
  }
}

export async function getSalesMeetings(params: { from: string; to: string }): Promise<MeetingsResponse> {
  return meetingsRequest('Prospect meetings', (signal) => apiClient.get('/project-management/sales/meetings', { params, signal }));
}

// --- Sales Report (week/month historical view) ---

export type ReportPeriod = 'week' | 'month';

export interface ReportDealEntry {
  id: string;
  name: string;
  valueEur: number;
  companyName: string | null;
  closedAt: string | null;
  stage: string | null;
  createdAt: string | null;
  kind: DealKind;
  /** Attio deal type as written ("New", "Renewal", "Automation", …). */
  attioType: string | null;
}

export interface ReportDealsSection {
  closedWonCount: number;
  closedWonValueEur: number;
  /** Closed won by type; `new` (excl. pilots) is the headline. */
  closedWonByKind: ByKind;
  closedLostCount: number;
  closedLostValueEur: number;
  /** Created with Attio type "New" (incl. pilots typed New) — the "New deals" headline. */
  createdNewCount: number;
  createdNewValueEur: number;
  createdNewPilotCount: number;
  createdByKind: ByKind;
  createdCount: number;
  createdValueEur: number;
  topClosed: ReportDealEntry[];
  closedWon: ReportDealEntry[];
  closedLost: ReportDealEntry[];
  created: ReportDealEntry[];
}

export interface ReportMeetingsSection {
  /** External AE meetings held; a joint meeting counts once. */
  total: number;
  /** Per AE; a joint meeting counts for each AE attending. */
  byRep: Array<{ repSlug: SalesRepSlug; count: number }>;
}

export interface ReportPipelineTotals {
  openCount: number;
  openValueEur: number;
  wonCount: number;
  wonValueEur: number;
  lostCount: number;
  /** Legacy: only on snapshots taken before 2026-10. */
  avgOpenDealEur?: number;
}

export interface ReportPipelineStageDelta {
  stage: string;
  startCount: number;
  startValueEur: number;
  endCount: number;
  endValueEur: number;
}

export interface ReportPipelineSection {
  /** Mondays whose snapshots the change is measured between. */
  startSnapshotDay: string;
  endSnapshotDay: string;
  /** The period is still running — no change shown. */
  incomplete: boolean;
  endOfPeriod: ReportPipelineTotals | null;
  startOfPeriod: ReportPipelineTotals | null;
  byStage: ReportPipelineStageDelta[];
  openValueDeltaEur: number | null;
  /** True when at least one backing snapshot was back-dated (current state tagged with a past Monday). */
  historical: boolean;
}

export interface ReportPeriodBucket {
  start: string;
  end: string;
  label: string;
  deals: ReportDealsSection;
  meetings: ReportMeetingsSection;
  pipeline: ReportPipelineSection;
}

export interface SalesReportWarning {
  source: 'meetings' | 'snapshots';
  message: string;
}

export interface SalesReport {
  generatedAt: string;
  period: ReportPeriod;
  buckets: ReportPeriodBucket[];
  warnings: SalesReportWarning[];
}

export async function getSalesReport(params: { period: ReportPeriod; count: number }): Promise<SalesReport> {
  return apiClient.get('/project-management/sales/report', { params });
}

// --- SDR bookings (the three personal Fillout forms) ---

export interface SdrSyncState {
  lastSyncedAt: string | null;
  /** The newest sync run failed (or has been running over an hour). `since`: first failed run after the last good one. */
  syncFailure: { at: string; error: string | null; since?: string } | null;
  /** The newest sync run is still running. Older APIs omit it. */
  syncRunning?: { since: string } | null;
}

export interface SdrOverviewTotals {
  thisWeek: number;
  lastWeek: number;
  /** Last week over the weekdays this week has had so far. Older APIs omit it. */
  lastWeekToDate?: number;
  thisMonth: number;
  lastMonth: number;
  sinceStart: number;
}

export interface SdrWeeklyPoint {
  weekStart: string;
  /** The days the bar counts: the week clipped to the page period. Older APIs omit them. */
  from?: string;
  to?: string;
  total: number;
}

export interface SdrBooking {
  submissionId: string;
  submittedAt: string;
  /** Brussels calendar day, YYYY-MM-DD. */
  day: string;
  personName: string | null;
  personEmail: string | null;
  companyName: string | null;
  /** The AE whose personal form was used. Older APIs omit it. */
  aeSlug?: SalesRepSlug | null;
  /** A Fillout submission, or a meeting Walter confirmed was SDR-booked without a form. Older APIs omit it (= form). */
  source?: 'form' | 'confirmed_without_form';
  /** The Calendly demo it became; null when none. Older APIs omit it. */
  meeting?: { startAt: string; status: 'active' | 'canceled' } | null;
}

export interface SdrOverview extends SdrSyncState {
  startDay: string;
  /** What one SDR meeting is; Demo bookings states the same sentence. Older APIs omit it. */
  definition?: string;
  /** SDR meetings in the last 90 days (Brussels), the Demo bookings default window. Older APIs omit it. */
  last90Days?: number;
  totals: SdrOverviewTotals;
  weeklySeries: SdrWeeklyPoint[];
  recentBookings: SdrBooking[];
  excluded: { beforeStart: number; tests: number; duplicates: number };
  /** Newest counted booking's submission time. Older APIs omit it. */
  newestSubmissionAt?: string | null;
  /** Bookings in the requested period (Brussels days, inclusive). Null without a period; older APIs omit it. */
  period?: { fromDay: string; toDay: string; count: number; bookings: SdrBooking[] } | null;
}

export async function getSdrOverview(params?: { from: string; to: string }): Promise<SdrOverview> {
  return meetingsRequest('SDR meetings', (signal) =>
    apiClient.get('/project-management/sdr/overview', params ? { params, signal } : { signal }),
  );
}

// --- SDR activity (Apollo dialer calls, one SDR total) ---

export interface SdrActivityWeek {
  /** Monday, YYYY-MM-DD (Brussels week). */
  weekStart: string;
  /** Outbound calls. */
  dials: number;
  /** Outbound calls of conversationMinSeconds or more. */
  conversations: number;
}

export interface SdrActivity extends SdrSyncState {
  /** Last 12 weeks, oldest first; the last one is the current week. */
  weeks: SdrActivityWeek[];
  thisWeek: SdrActivityWeek;
  /** The last complete week. */
  lastWeek: SdrActivityWeek;
  conversationMinSeconds: number;
}

export async function getSdrActivity(): Promise<SdrActivity> {
  return apiClient.get('/project-management/sdr/activity');
}

